import { httpServiceClient } from '@/infra/http'
import {
  AuthorizationFilterParams,
  AuthorizationPaginatedResponse,
  AuthorizationItem,
  AuthorizationHistoryResponse,
  AuthorizationStatusGroup,
  AuthorizationStatus,
  AuthorizationLabel,
  AuthorizationLabelRelation,
  FilterOptions,
  CourseSkill,
  ApproveRejectPayload,
  EnrollmentAcceptedPayload,
  SuperadminRejectedPayload,
  ChangeStatusPayload,
  AddCommentPayload,
  UpdateLabelsPayload,
  InstitutionPaymentDefaultPayload,
  InstitutionPaymentTransferPayload,
  InstitutionPaymentBoletoPayload,
  InstitutionPaymentAttachmentPayload,
  SchedulePassPaymentPayload,
  ScheduleTrainingPaymentPayload,
  SendEmailPayload,
  CancelRefundPayload
} from './authorization-service-types'
import {
  buildFilterQueryParams,
  createFormDataWithFile,
  formatAuthorizationList,
  formatAuthorizationItem,
  formatAuthorizationHistory,
  formatStatusGroups
} from './authorization-service-utils'
import { makeApiUrl } from '@/main/factories/http'
import { enrollmentStepsService } from '../enrollment/enrollment-steps-service'

export const createAuthorizationService = (client = httpServiceClient) => {
  const fetchById = async (id: number) => {
    const response = await client<AuthorizationItem>({
      method: 'get',
      url: makeApiUrl(`/authorization/${id}`)
    })
    return formatAuthorizationItem(response.body.data as AuthorizationItem)
  }

  const fetchHistory = async (authId: number) => {
    const response = await client<AuthorizationHistoryResponse>({
      method: 'get',
      url: makeApiUrl(`/authorization_history/${authId}`)
    })
    return formatAuthorizationHistory(response.body.data as AuthorizationHistoryResponse)
  }

  const fetchCourseSkills = async (userId: number, courseId: number) => {
    const response = await client<CourseSkill[]>({
      method: 'get',
      url: makeApiUrl(`/course/get_skills/${userId}/${courseId}`)
    })
    return response.body.data as CourseSkill[]
  }

  const fetchLabels = async () => {
    const response = await client<AuthorizationLabel[]>({
      method: 'get',
      url: makeApiUrl('/authorization_label')
    })
    return response.body.data as AuthorizationLabel[]
  }

  return {
    // ==================== READ OPERATIONS ====================

    async fetchList(params: AuthorizationFilterParams, signal?: AbortSignal) {
      const response = await client<AuthorizationPaginatedResponse>({
        method: 'get',
        url: makeApiUrl('/authorization'),
        params: buildFilterQueryParams(params),
        signal
      })

      return formatAuthorizationList(response.body.data as AuthorizationPaginatedResponse)
    },

    fetchById,

    async fetchAuthorizationDetail(authorizationId: number) {
      const historyData = await fetchHistory(authorizationId)

      const [authorization, skills, availableLabels, requestDetails] = await Promise.all([
        fetchById(authorizationId),
        fetchCourseSkills(
          historyData.authorizationInfo.userId,
          historyData.authorizationInfo.courseId
        ).catch(() => []),
        fetchLabels().catch(() => []),
        enrollmentStepsService.fetchEnrollmentStepsDetail(String(authorizationId))
      ])

      return {
        authorizationDetail: {
          ...authorization,
          ...historyData.authorizationInfo,
          ...authorization,
          authorizationHistoryList: historyData.authorizationHistoryList,
          payableAuthorization: historyData.payableAuthorization,
          rating: historyData.rating,
          skills
        },
        availableLabels,
        requestDetails
      }
    },

    fetchHistory,

    async fetchStatusGroups() {
      const response = await client<AuthorizationStatusGroup[]>({
        method: 'get',
        url: makeApiUrl('/authorization_status/groups')
      })

      return formatStatusGroups(response.body.data as unknown as Array<{ [key: string]: unknown }>)
    },

    async fetchAllStatuses() {
      const response = await client<AuthorizationStatus[]>({
        method: 'get',
        url: makeApiUrl('/authorization_status')
      })

      return response.body.data as AuthorizationStatus[]
    },

    fetchLabels,

    async fetchLabelsByAuthId(id: number) {
      const response = await client<AuthorizationLabelRelation[]>({
        method: 'get',
        url: makeApiUrl(`/authorization_label/${id}`)
      })

      return response.body.data as AuthorizationLabelRelation[]
    },

    async fetchFilterOptions() {
      const response = await client<FilterOptions>({
        method: 'get',
        url: makeApiUrl('/user/statusOptions')
      })

      return response.body.data as FilterOptions
    },

    fetchCourseSkills,

    async checkRefundInPlan(id: number) {
      const response = await client<boolean>({
        method: 'get',
        url: makeApiUrl(`/authorization-refund/${id}/in-user-plan`)
      })

      return response.body.data as boolean
    },

    async fetchCompanySharedBudget() {
      const response = await client<{ budget: number }>({
        method: 'get',
        url: makeApiUrl('/company/shared-budget')
      })

      return (response.body.data as { budget: number })?.budget || 0
    },

    // ==================== WRITE OPERATIONS ====================

    async approveAuthorization(payload: ApproveRejectPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization/authorization_accepted'),
        body: createFormDataWithFile(payload as unknown as Record<string, unknown>)
      })
    },

    async rejectAuthorization(payload: ApproveRejectPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization/authorization_rejected'),
        body: createFormDataWithFile(payload as unknown as Record<string, unknown>)
      })
    },

    async enrollmentAccepted(payload: EnrollmentAcceptedPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization/enrollment_accepted'),
        body: createFormDataWithFile(payload as unknown as Record<string, unknown>)
      })
    },

    async superadminRejected(payload: SuperadminRejectedPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization/superadmin_rejected'),
        body: createFormDataWithFile(payload as unknown as Record<string, unknown>)
      })
    },

    async enrollmentConfirmation(authId: number, confirmation: boolean) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization/enrollment_confirmation'),
        body: { authId, confirmation }
      })
    },

    async sendWrongDocument(authId: number, message: string) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization/wrong-document'),
        body: { authId, customMessage: message }
      })
    },

    async changeStatus(payload: ChangeStatusPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl(`/v2/admin/authorization/${payload.id}/status`),
        body: { statusId: payload.statusId, comment: payload.comment }
      })
    },

    async addComment(payload: AddCommentPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization_history'),
        body: createFormDataWithFile(payload as unknown as Record<string, unknown>)
      })
    },

    async updateLabels(payload: UpdateLabelsPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization_label'),
        body: { id: payload.id, labelsId: payload.labelsId }
      })
    },

    async uploadNF(data: FormData) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization_nf'),
        body: data
      })
    },

    async deleteNF(id: number) {
      await client<void>({
        method: 'delete',
        url: makeApiUrl(`/authorization_nf/${id}`)
      })
    },

    async submitBoleto(data: FormData) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/submit_boleto'),
        body: data
      })
    },

    async institutionPaymentDefault(payload: InstitutionPaymentDefaultPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization_institution_payment/default'),
        body: payload
      })
    },

    async institutionPaymentTransfer(payload: InstitutionPaymentTransferPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization_institution_payment/transfer'),
        body: payload
      })
    },

    async institutionPaymentBoleto(payload: InstitutionPaymentBoletoPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization_institution_payment/boleto'),
        body: payload
      })
    },

    async institutionPaymentAttachment(payload: InstitutionPaymentAttachmentPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization_institution_payment/attachment'),
        body: createFormDataWithFile(payload as unknown as Record<string, unknown>)
      })
    },

    async deleteInstitutionPayment(authId: number) {
      await client<void>({
        method: 'delete',
        url: makeApiUrl(`/authorization_institution_payment/${authId}`)
      })
    },

    async resendEmail(authId: number) {
      await client<void>({
        method: 'post',
        url: makeApiUrl(`/authorization/${authId}/resend-email`)
      })
    },

    // ==================== SKILLPASS PAYMENT ====================

    async checkPaymentScheduled(authId: number) {
      try {
        await client<void>({
          method: 'get',
          url: makeApiUrl(`/authorization_pass_payment/${authId}`)
        })
        return true
      } catch {
        return false
      }
    },

    async schedulePassPayment(payload: SchedulePassPaymentPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization_pass_payment'),
        body: payload
      })
    },

    async cancelPassPayment(authId: number) {
      await client<void>({
        method: 'delete',
        url: makeApiUrl(`/authorization_pass_payment/${authId}`)
      })
    },

    // ==================== TRAINING PAYMENT & COMPLIANCE ====================

    async scheduleTrainingPayment(payload: ScheduleTrainingPaymentPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization_training_payment'),
        body: payload
      })
    },

    async cancelTrainingPayment(authId: number) {
      await client<void>({
        method: 'delete',
        url: makeApiUrl(`/authorization_training_payment/${authId}`)
      })
    },

    async deletePaymentProof(authId: number) {
      await client<void>({
        method: 'delete',
        url: makeApiUrl(`/authorization_payment_proof/${authId}`)
      })
    },

    // ==================== EMAIL & REFUND ====================

    async sendEmailByType(payload: SendEmailPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization/send-email'),
        body: payload
      })
    },

    async cancelRefund(payload: CancelRefundPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl(`/authorization_refund/cancel/${payload.authId}`),
        body: { comment: payload.comment }
      })
    }
  }
}

export const authorizationService = createAuthorizationService()
